import { PayloadAction } from '@reduxjs/toolkit';
import { call, put } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/types';
import { amlbotAPI } from '@/api';
import type {
  GetNotificationsRequest,
  GetNotificationsResponse,
  GetUnreadCountResponse,
  MarkNotificationReadRequest,
} from '@/api';
import { parseError } from '@/redux/helpers';
import { notificationsActions } from '@/redux/actions';

function* fetchNotifications(
  action: PayloadAction<{ cursor?: string } | undefined>,
): SagaIterator {
  try {
    const cursor = action.payload?.cursor;
    const request: GetNotificationsRequest = {
      limit: 20,
      ...(cursor ? { cursor } : {}),
    };
    const response: GetNotificationsResponse = yield call(
      amlbotAPI.fetchNotifications,
      request,
    );

    // A cursor means "load more" — append. No cursor means a fresh open — replace.
    if (cursor) {
      yield put(notificationsActions.appendData(response.data));
    } else {
      yield put(notificationsActions.setData(response.data));
    }
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(notificationsActions.setError(message));
  } finally {
    yield put(notificationsActions.setLoading(false));
  }
}

function* fetchUnreadCount(): SagaIterator {
  try {
    const response: GetUnreadCountResponse = yield call(
      amlbotAPI.fetchUnreadCount,
      {},
    );
    yield put(notificationsActions.setUnreadCount(response.data.count));
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(notificationsActions.setError(message));
  }
}

function* markRead(
  action: PayloadAction<MarkNotificationReadRequest>,
): SagaIterator {
  try {
    // Optimistic: the badge should drop the instant the user clicks.
    yield put(notificationsActions.markReadSuccess(action.payload));
    yield call(amlbotAPI.markNotificationRead, action.payload);
  } catch (error: unknown) {
    // Re-sync from the server rather than trying to invert the optimistic update.
    const { message } = parseError(error);
    yield put(notificationsActions.setError(message));
    yield put(notificationsActions.fetchUnreadCount());
  }
}

function* markAllRead(): SagaIterator {
  try {
    yield put(notificationsActions.markAllReadSuccess());
    yield call(amlbotAPI.markAllNotificationsRead, {});
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(notificationsActions.setError(message));
    yield put(notificationsActions.fetchUnreadCount());
  }
}

export { fetchNotifications, fetchUnreadCount, markRead, markAllRead };
